import { useState, useRef, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { compose, tap, always } from 'ramda';

import { useErrorNotifier } from 'shared/custom-hooks/useNotifiers';
import { CobraDateRangeCounts } from '../cobra-report-types';
import { getCobraCountsByDateRange } from 'shared/api/cobraDateRangeReportsApi';
import { selectSelectedCompany } from 'companies/companies.slice';

export const defaultCounts: CobraDateRangeCounts = {
  noticesMailed: 0,
  pendingNotices: 0,
  paymentsReceived: 0,
  currentActive: 0,
  monthlyRecap: 0,
  acceptancesReceived: 0,
  invoiceStatus: 0,
  initialNoticesSent: 0,
  activeWithAddress: 0,
  noResponseWithAddress: 0,
  termsFromCobra: 0,
  ten95cData: 0,
};

export function useCobraReportCounts(startDate: string, endDate: string) {
  const errorNotifier = useErrorNotifier();
  const { compid } = useSelector(selectSelectedCompany) ?? { compid: 0 };

  const [ reportCounts, setReportCounts ] = useState<CobraDateRangeCounts | null>(null);

  const [ setDefault ] = useState(() => compose(setReportCounts, always(defaultCounts)));
  const [ handleError ] = useState(() => compose(tap(setDefault), errorNotifier));
  const mountedRef = useRef(false);

  useEffect(() => {
    mountedRef.current = true;

    if (compid > 0 && !!startDate && !!endDate) {
      setReportCounts(null);
      getCobraCountsByDateRange(compid, startDate, endDate)
        .then((result) => {
          if (mountedRef.current) {
            setReportCounts(result ?? defaultCounts);
          }
        })
        .catch((err) => {
          if (mountedRef.current) {
            handleError(err);
          }
        });
    }

    return () => {
      mountedRef.current = false;
    };
  }, [ handleError, compid, startDate, endDate ]);

  return reportCounts;
}
